import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useToast } from '../../components/ui/Toast';
import Shell from '../dashboard/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import './OfferPage.css';

const OfferPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { getAxios, user } = useSecureAuth();

  const promoCode = searchParams.get('code');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promoData, setPromoData] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('MONTHLY');
  const [processingPayment, setProcessingPayment] = useState(false);

  const validatePromo = useCallback(async () => {
    if (!promoCode) {
      setError('No promo code provided. Please use a valid offer link.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const axios = getAxios();
      const response = await axios.post('/merchant/billing/validate-promo', {
        code: promoCode
      });

      if (response.data.success) {
        setPromoData(response.data);
      } else {
        setError(response.data.error || 'Invalid promo code');
      }
    } catch (err) {
      console.error('Promo validation error:', err);
      setError(err.response?.data?.error || 'Failed to validate promo code');
    } finally {
      setLoading(false);
    }
  }, [promoCode, getAxios]);

  useEffect(() => {
    validatePromo();
  }, [validatePromo]);

  const handleSelectTier = (tier) => {
    setSelectedTier(tier);
  };

  const handleSubscribe = async () => {
    if (!selectedTier) {
      toast.error('Please select a plan');
      return;
    }

    setProcessingPayment(true);

    try {
      const axios = getAxios();

      // Use the initiate-tier-change endpoint with promo code
      const response = await axios.post('/merchant/billing/initiate-tier-change', {
        newTier: selectedTier.tier,
        action: 'upgrade',
        amount: selectedTier.originalPrice,
        currency: selectedTier.currency || 'NGN',
        requiresPayment: true,
        promoCode: promoCode
      });

      if (response.data.authorizationUrl) {
        // Redirect to Paystack
        window.location.href = response.data.authorizationUrl;
      } else if (response.data.success) {
        toast.success('Subscription activated successfully!');
        navigate('/settings?tab=billing');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      toast.error(err.response?.data?.error || 'Failed to process subscription');
    } finally {
      setProcessingPayment(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Error state
  if (error) {
    return (
      <Shell>
        <div className="offer-page">
          <div className="offer-error">
            <div className="error-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h2>Offer Not Available</h2>
            <p>{error}</p>
            <Button onClick={() => navigate('/upgrade')} variant="primary">
              View All Plans
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Shell>
        <div className="offer-page">
          <div className="offer-loading">
            <div className="loading-spinner"></div>
            <p>Validating your offer...</p>
          </div>
        </div>
      </Shell>
    );
  }

  const { promo, applicableTiers, currentTier, isOnTrial } = promoData || {};

  return (
    <Shell>
      <div className="offer-page">
        {/* Promo Header */}
        <div className="offer-header">
          <div className="promo-badge">
            <span className="promo-icon">🎉</span>
            <span>Special Offer</span>
          </div>
          <h1>{promo?.name || 'Exclusive Discount'}</h1>
          {promo?.description && <p className="promo-description">{promo.description}</p>}

          <div className="discount-highlight">
            <span className="discount-value">
              {promo?.discountType === 'PERCENTAGE'
                ? `${promo.discountValue}% OFF`
                : formatPrice(promo?.discountValue)}
            </span>
            {promo?.maxDiscountAmount && promo?.discountType === 'PERCENTAGE' && (
              <span className="max-discount">
                (up to {formatPrice(promo.maxDiscountAmount)})
              </span>
            )}
          </div>

          {promo?.validUntil && (
            <p className="offer-expiry">
              Offer valid until {formatDate(promo.validUntil)}
            </p>
          )}
        </div>

        {/* Current Status */}
        <div className="current-status">
          <Badge variant={isOnTrial ? 'warning' : 'outline'}>
            {isOnTrial ? `On Trial: ${currentTier}` : `Current Plan: ${currentTier}`}
          </Badge>
        </div>

        {/* Tier Selection */}
        <div className="tier-selection">
          <h2>Choose Your Plan</h2>
          <p>Select a plan to apply your discount</p>

          <div className="tiers-grid">
            {applicableTiers?.map((tier) => (
              <div
                key={tier.tier}
                className={`tier-card ${selectedTier?.tier === tier.tier ? 'selected' : ''}`}
                onClick={() => handleSelectTier(tier)}
              >
                <div className="tier-header">
                  <h3>{tier.name}</h3>
                  <Badge variant={tier.tier === 'PRO' ? 'success' : 'primary'}>
                    {tier.tier}
                  </Badge>
                </div>

                <div className="tier-pricing">
                  <div className="original-price">
                    <span className="strikethrough">{formatPrice(tier.originalPrice)}</span>
                    <span className="period">/month</span>
                  </div>
                  <div className="discounted-price">
                    <span className="price">{formatPrice(tier.discountedPrice)}</span>
                    <span className="period">/month</span>
                  </div>
                  <div className="savings">
                    You save {formatPrice(tier.discountAmount)}
                  </div>
                </div>

                {tier.features && tier.features.length > 0 && (
                  <ul className="tier-features">
                    {tier.features.slice(0, 5).map((feature, idx) => (
                      <li key={idx}>
                        <svg className="check-icon" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M6.66667 10.1147L12.7947 3.98599L13.738 4.92866L6.66667 12L2.42267 7.75599L3.36533 6.81333L6.66667 10.1147Z"
                            fill="currentColor"
                          />
                        </svg>
                        {feature.replace(/_/g, ' ')}
                      </li>
                    ))}
                  </ul>
                )}

                {selectedTier?.tier === tier.tier && (
                  <div className="selected-indicator">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Summary & Action */}
        {selectedTier && (
          <Card className="order-summary">
            <Card.Header>
              <Card.Title>Order Summary</Card.Title>
            </Card.Header>
            <div className="summary-content">
              <div className="summary-row">
                <span>Plan</span>
                <span>{selectedTier.name} ({selectedTier.tier})</span>
              </div>
              <div className="summary-row">
                <span>Original Price</span>
                <span className="strikethrough">{formatPrice(selectedTier.originalPrice)}</span>
              </div>
              <div className="summary-row discount">
                <span>Discount ({promo?.code})</span>
                <span>-{formatPrice(selectedTier.discountAmount)}</span>
              </div>
              <div className="summary-row total">
                <span>Total (First Month)</span>
                <span>{formatPrice(selectedTier.discountedPrice)}</span>
              </div>
              <p className="renewal-note">
                * After the first month, your subscription will renew at {formatPrice(selectedTier.originalPrice)}/month
              </p>
            </div>
            <div className="summary-actions">
              <Button
                variant="primary"
                size="lg"
                onClick={handleSubscribe}
                disabled={processingPayment}
                className="subscribe-button"
              >
                {processingPayment ? 'Processing...' : `Subscribe for ${formatPrice(selectedTier.discountedPrice)}`}
              </Button>
            </div>
          </Card>
        )}

        {/* Fine Print */}
        <div className="offer-terms">
          <p>
            This is a one-time discount that applies to your first subscription payment only.
            By subscribing, you agree to our terms of service.
          </p>
        </div>
      </div>
    </Shell>
  );
};

export default OfferPage;
